import { useStaticQuery, graphql } from 'gatsby';
import * as React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTwitter,
  faFacebook,
  faInstagram,
  faYoutube,
  faSpotify,
  faSoundcloud,
} from '@fortawesome/free-brands-svg-icons';
import { faEnvelope } from '@fortawesome/free-regular-svg-icons';
import SiteLogo from './SiteLogo';
import { notEmpty } from '../../utils/common';
import MenuLink from '../MenuLink';
import SearchContext from '../../contexts/SearchContext';

const Wrapper = styled.footer<{ $isSearchOpen: boolean; $hasMarginTop: boolean }>`
  display: ${({ $isSearchOpen }) => ($isSearchOpen ? 'none' : 'block')};
  margin-top: ${({ $hasMarginTop }) => ($hasMarginTop ? '80px' : '0')};
  padding: 60px 0 113px;
  border-top: 0.5px solid var(--earth-fm--color--green);

  // Hide footer in the desktop app
  .electron-app & {
    display: none;
  }

  .container {
    display: flex;
    flex-direction: column;
  }

  @media (min-width: 992px) {
    margin-top: ${({ $hasMarginTop }) => ($hasMarginTop ? '120px' : '0')};
    padding: 50px 0 136px;

    .container {
      flex-direction: row;
      gap: clamp(120px, 12vw, 180px);
    }
  }
`;

const FooterLogo = styled.div`
  @media (min-width: 992px) {
    flex: 0 0 110px;
  }
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  margin-top: 55px;

  div {
    &:not(:first-of-type) {
      margin-top: 25px;
    }
  }

  a {
    text-decoration: none;
  }

  @media (min-width: 768px) {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 24px;

    div {
      width: calc(50% - 12px);
      max-width: 394px;

      &:not(:first-of-type) {
        margin-top: 0;
      }
    }
  }

  @media (min-width: 992px) {
    flex: 1;
    max-width: 800px;
    margin-top: 0;
  }
`;

const SocialLinks = styled.nav`
  display: flex;
  margin-bottom: 30px;
  order: -1;
  font-size: 16px;

  a {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 5px;
    background-color: var(--earth-fm--color--green);
    color: #fff;

    &:not(:first-of-type) {
      margin-left: 20px;
    }
  }

  @media (min-width: 992px) {
    order: 3;
    margin: 0 0 0 auto;

    a {
      &:not(:first-of-type) {
        margin-left: 10px;
      }
    }
  }
`;

interface Props {
  hasMarginTop?: boolean;
}

export default function Footer({ hasMarginTop = true }: Props) {
  const { isSearchOpen } = React.useContext(SearchContext);

  const data = useStaticQuery<Queries.FooterQuery>(graphql`
    query Footer {
      wpMenu(locations: { eq: FOOTER_MENU }) {
        menuItems {
          nodes {
            id
            label
            uri
            target
          }
        }
      }
      wp {
        themeGeneralSettings {
          themeOptions {
            socialLinks {
              email
              facebook
              instagram
              soundcloud
              spotify
              twitter
              youtube
            }
          }
        }
      }
    }
  `);

  const socialLinks = data.wp?.themeGeneralSettings?.themeOptions?.socialLinks;

  return (
    <Wrapper $isSearchOpen={isSearchOpen} $hasMarginTop={hasMarginTop}>
      <div className="container">
        <FooterLogo>
          <SiteLogo lazyLoading />
        </FooterLogo>
        <Nav>
          {data.wpMenu?.menuItems?.nodes
            ? data.wpMenu.menuItems.nodes.filter(notEmpty).map((menuItem) => (
                <div key={menuItem.id}>
                  <MenuLink to={menuItem.uri || '#'} target={menuItem.target || undefined}>
                    {menuItem.label}
                    {menuItem.target !== undefined && (
                      <span className="screen-reader-text">Open in a new tab</span>
                    )}
                  </MenuLink>
                </div>
              ))
            : null}
        </Nav>
        {socialLinks && Object.values(socialLinks).filter((url) => url !== null).length > 0 && (
          <SocialLinks>
            {socialLinks.twitter && (
              <a href={socialLinks.twitter} style={{ backgroundColor: '#1da1f2' }}>
                <FontAwesomeIcon icon={faTwitter} />
                <span className="screen-reader-text">Twitter</span>
              </a>
            )}
            {socialLinks.facebook && (
              <a href={socialLinks.facebook} style={{ backgroundColor: '#1877f2' }}>
                <FontAwesomeIcon icon={faFacebook} />
                <span className="screen-reader-text">Facebook</span>
              </a>
            )}
            {socialLinks.instagram && (
              <a href={socialLinks.instagram} style={{ backgroundColor: '#f00075' }}>
                <FontAwesomeIcon icon={faInstagram} />
                <span className="screen-reader-text">Instagram</span>
              </a>
            )}
            {socialLinks.youtube && (
              <a href={socialLinks.youtube} style={{ backgroundColor: '#ff0000' }}>
                <FontAwesomeIcon icon={faYoutube} />
                <span className="screen-reader-text">YouTube</span>
              </a>
            )}
            {socialLinks.spotify && (
              <a href={socialLinks.spotify} style={{ backgroundColor: '#1db954' }}>
                <FontAwesomeIcon icon={faSpotify} />
                <span className="screen-reader-text">Spotify</span>
              </a>
            )}
            {socialLinks.soundcloud && (
              <a href={socialLinks.soundcloud} style={{ backgroundColor: '#ff8800' }}>
                <FontAwesomeIcon icon={faSoundcloud} />
                <span className="screen-reader-text">SoundCloud</span>
              </a>
            )}
            {socialLinks.email && (
              <a href={`mailto:${socialLinks.email}`}>
                <FontAwesomeIcon icon={faEnvelope} />
                <span className="screen-reader-text">Email us</span>
              </a>
            )}
          </SocialLinks>
        )}
      </div>
    </Wrapper>
  );
}
